/**
 * LMS HTTP layer. Three responsibilities:
 *   1. Boot-time handoff: read a short-lived JWT from the URL fragment,
 *      exchange it for a session token, stash that token in sessionStorage
 *      (origin-bound), then scrub the fragment from the URL.
 *   2. Project I/O: fetch and save .sb3 blobs against the backend the
 *      handoff token came from (the api_base claim drives routing).
 *   3. Token cache: every subsequent request sends Authorization: Bearer.
 *
 * The same scratch-gui bundle serves both audiences (students via
 * portal.shuleone.co.ke + ElearningApi, teachers via app.shuleone.co.ke +
 * NewFeeGemini); the api_base claim distinguishes which backend to talk to.
 */

import parseJwt from './parse-jwt';

const STORAGE_KEY_TOKEN     = 'lms.session.token';
const STORAGE_KEY_API_BASE  = 'lms.session.api_base';
const STORAGE_KEY_READONLY  = 'lms.session.read_only';
const STORAGE_KEY_PROJECTID = 'lms.session.project_id';
const STORAGE_KEY_RETURN_TO = 'lms.session.return_to';

let cachedToken     = null;
let cachedApiBase   = null;
let cachedReadOnly  = false;
let cachedProjectId = null;
let cachedReturnTo  = null;

/**
 * Run on page boot before mounting the GUI. Returns a promise that resolves
 * with `{projectId, readOnly}` once the session token is in place — or
 * rejects with an Error suitable for displaying as a friendly "please log
 * back into the LMS" screen.
 *
 * Two paths:
 *   a) URL fragment carries `#h=<handoff-jwt>` (fresh boot from LMS) — we
 *      exchange it, persist the long-lived token, and scrub the fragment.
 *   b) sessionStorage already has a session token (refresh after handoff) —
 *      reuse it; ignore any stale fragment.
 */
export const bootstrap = async () => {
    const stored = readStoredSession();
    const handoff = readHandoffFromHash();

    // Pick up the LMS's origin from ?returnTo=... if present, so the menu
    // bar can render a "Back to LMS" link. Persist it for the lifetime of
    // this session so a page refresh in Scratch doesn't lose the breadcrumb.
    const urlReturnTo = readReturnToFromQuery();
    if (urlReturnTo) {
        cachedReturnTo = urlReturnTo;
        try { window.sessionStorage.setItem(STORAGE_KEY_RETURN_TO, urlReturnTo); } catch (e) { /* private mode */ }
    } else {
        try { cachedReturnTo = window.sessionStorage.getItem(STORAGE_KEY_RETURN_TO) || null; } catch (e) { cachedReturnTo = null; }
    }

    // Always wipe the fragment + returnTo param, even on refresh — keeps
    // the token off the address bar and the URL clean.
    if (window.location.hash || urlReturnTo) {
        const u = new URL(window.location.href);
        u.searchParams.delete('returnTo');
        // history.replaceState ignores fragments we strip manually.
        const target = u.pathname + (u.search || '');
        window.history.replaceState(window.history.state, '', target);
    }

    if (handoff) {
        await exchange(handoff);
    } else if (stored) {
        cachedToken     = stored.token;
        cachedApiBase   = stored.apiBase;
        cachedReadOnly  = stored.readOnly;
        cachedProjectId = stored.projectId;
    } else {
        throw new Error('No LMS session — open this editor through the LMS.');
    }

    // Prefer the JWT's pid claim (authoritative); fall back to a /p/N URL
    // segment if present (purely cosmetic). On localhost dev the URL is
    // /lms.html so the path won't match and we use the JWT exclusively.
    const fromPath = readProjectIdFromPath();
    return {
        projectId: cachedProjectId != null ? cachedProjectId : fromPath,
        readOnly: cachedReadOnly
    };
};

const readHandoffFromHash = () => {
    const h = window.location.hash.replace(/^#/, '');
    if (!h) return null;
    const params = new URLSearchParams(h);
    return params.get('h');
};

const readReturnToFromQuery = () => {
    try {
        const v = new URLSearchParams(window.location.search).get('returnTo');
        if (!v) return null;
        // Only honour http(s) URLs to avoid javascript:/data: shenanigans
        // if a JWT ever leaked an attacker-controlled returnTo.
        if (!/^https?:\/\//i.test(v)) return null;
        return v;
    } catch (e) {
        return null;
    }
};

const readStoredSession = () => {
    const token = window.sessionStorage.getItem(STORAGE_KEY_TOKEN);
    const apiBase = window.sessionStorage.getItem(STORAGE_KEY_API_BASE);
    if (!token || !apiBase) return null;
    const pidStr = window.sessionStorage.getItem(STORAGE_KEY_PROJECTID);
    return {
        token,
        apiBase,
        readOnly: window.sessionStorage.getItem(STORAGE_KEY_READONLY) === '1',
        projectId: pidStr == null || pidStr === '' ? null : Number(pidStr)
    };
};

/** Cosmetic: `/p/123` -> 123, otherwise null. The JWT's pid claim is the
 *  authoritative source; the path is only used when the JWT didn't carry
 *  one (rare — only when the LMS opens a brand-new sandbox). */
const readProjectIdFromPath = () => {
    const m = window.location.pathname.match(/^\/p\/(\d+)\/?$/);
    return m ? Number(m[1]) : null;
};

/**
 * Exchange the short-lived handoff token for a session-length token. The
 * handoff token's `api_base` claim tells us which backend to call.
 */
const exchange = async handoffToken => {
    const claims = parseJwt(handoffToken);
    const apiBase   = claims.api_base;
    // Backend mints with claim names `ro` (boolean) and `pid` (long); see
    // ScratchHandoffService.CLAIM_READ_ONLY / CLAIM_PROJECT.
    const readOnly  = !!claims.ro;
    const projectId = claims.pid == null ? null : Number(claims.pid);
    if (!apiBase) throw new Error('Handoff token missing api_base claim');

    const res = await fetch(`${apiBase}/exchange`, {
        method: 'POST',
        headers: {Authorization: `Bearer ${handoffToken}`}
    });
    if (!res.ok) throw new Error(`Handoff exchange failed: HTTP ${res.status}`);

    const {token} = await res.json();
    if (!token) throw new Error('Handoff exchange returned no token');

    cachedToken     = token;
    cachedApiBase   = apiBase;
    cachedReadOnly  = readOnly;
    cachedProjectId = projectId;

    window.sessionStorage.setItem(STORAGE_KEY_TOKEN,     token);
    window.sessionStorage.setItem(STORAGE_KEY_API_BASE,  apiBase);
    window.sessionStorage.setItem(STORAGE_KEY_READONLY,  readOnly ? '1' : '0');
    window.sessionStorage.setItem(STORAGE_KEY_PROJECTID, projectId == null ? '' : String(projectId));
};

const authHeaders = () => ({Authorization: `Bearer ${cachedToken}`});

export const getApiBase  = () => cachedApiBase;
export const getReadOnly = () => cachedReadOnly;
export const getReturnTo = () => cachedReturnTo;

/**
 * GET /projects/{id}/file → ArrayBuffer of the stored .sb3.
 * Returns null on 404, signalling "this project exists but has never been
 * saved yet" — the caller should load the bundled default project instead
 * so the editor still boots. Other non-OK statuses throw.
 */
export const fetchProject = async projectId => {
    const res = await fetch(`${cachedApiBase}/projects/${projectId}/file`, {
        headers: authHeaders()
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Fetch project ${projectId} failed: HTTP ${res.status}`);
    return res.arrayBuffer();
};

/**
 * PUT /projects/{id}/file with the .sb3 Blob.
 * For sandbox saves where projectId is null, POST /projects instead so the
 * backend can mint a new id; returns the new id.
 */
export const saveProject = async (projectId, sb3Blob, thumbnailBlob) => {
    const form = new FormData();
    form.append('sb3', sb3Blob, 'project.sb3');
    if (thumbnailBlob) form.append('thumbnail', thumbnailBlob, 'thumb.png');

    const url = projectId ?
        `${cachedApiBase}/projects/${projectId}/file` :
        `${cachedApiBase}/projects`;
    const method = projectId ? 'PUT' : 'POST';

    const res = await fetch(url, {method, headers: authHeaders(), body: form});
    if (!res.ok) throw new Error(`Save project failed: HTTP ${res.status}`);
    return res.json();
};
